import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useWorkspace } from '@/contexts/WorkspaceContext';

interface WorkspaceHealthMetric {
  id: string;
  workspace_id: string;
  component_name: string;
  status: 'healthy' | 'warning' | 'error' | 'maintenance';
  last_check_at: string;
  response_time_ms?: number;
  error_count: number;
  uptime_percentage: number;
  metadata: Record<string, any>;
}

interface WorkspaceHealthSummary {
  overall_status: 'healthy' | 'warning' | 'error' | 'maintenance';
  total_components: number;
  healthy_components: number;
  warning_components: number;
  error_components: number;
  last_updated: string;
  health_score: number;
}

export const useWorkspaceHealth = (autoRefresh = true) => {
  const { currentWorkspace } = useWorkspace();
  const queryClient = useQueryClient();
  const [isPerformingCheck, setIsPerformingCheck] = useState(false);

  const {
    data: healthMetrics = [],
    isLoading: isLoadingHealth,
    error: healthError
  } = useQuery({
    queryKey: ['workspace-health', currentWorkspace?.id],
    queryFn: async () => {
      if (!currentWorkspace?.id) return [];
      
      try {
        const { data, error } = await supabase.functions.invoke('workspace-health', {
          body: { workspace_id: currentWorkspace.id, action: 'get_metrics' }
        });
        
        if (error) {
          // Log but don't throw - return empty array instead
          console.warn('Workspace health check failed:', error.message);
          return [];
        }
        return data?.metrics || [];
      } catch (err) {
        // Catch network errors, auth errors etc.
        console.warn('Workspace health check error:', err);
        return [];
      }
    },
    enabled: !!currentWorkspace?.id,
    refetchInterval: autoRefresh ? 30000 : false, // 30 seconds
    staleTime: 20000, // Consider data fresh for 20 seconds
    retry: 1, // Only retry once to prevent console spam
  });

  const {
    data: healthSummary,
    isLoading: isLoadingSummary
  } = useQuery({
    queryKey: ['workspace-health-summary', currentWorkspace?.id],
    queryFn: async () => {
      if (!currentWorkspace?.id) return null;
      
      try {
        const { data, error } = await supabase.functions.invoke('workspace-health', {
          body: { workspace_id: currentWorkspace.id, action: 'get_summary' }
        });
        
        if (error) {
          console.warn('Workspace health summary failed:', error.message);
          return null;
        }
        return data?.summary;
      } catch (err) {
        console.warn('Workspace health summary error:', err);
        return null;
      }
    },
    enabled: !!currentWorkspace?.id,
    refetchInterval: autoRefresh ? 60000 : false, // 1 minute
    retry: 1, // Only retry once
  });

  const performHealthCheck = useMutation({
    mutationFn: async () => {
      if (!currentWorkspace?.id) throw new Error('No workspace selected');
      
      setIsPerformingCheck(true);
      const { data, error } = await supabase.functions.invoke('workspace-health', {
        body: { workspace_id: currentWorkspace.id, action: 'run_check' }
      });
      
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['workspace-health'] });
      queryClient.invalidateQueries({ queryKey: ['workspace-health-summary'] });
    },
    onSettled: () => {
      setIsPerformingCheck(false);
    }
  });

  const triggerRecovery = useMutation({
    mutationFn: async (componentName: string) => {
      if (!currentWorkspace?.id) throw new Error('No workspace selected');
      
      const { data, error } = await supabase.functions.invoke('workspace-health', {
        body: { 
          workspace_id: currentWorkspace.id, 
          action: 'trigger_recovery',
          component_name: componentName 
        }
      });
      
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['workspace-health'] });
    }
  });

  // Helper functions
  const getOverallStatus = (): 'healthy' | 'warning' | 'error' | 'maintenance' => {
    if (isLoadingHealth) return 'maintenance';
    if (healthError) return 'error';
    
    const errorCount = healthMetrics.filter(m => m.status === 'error').length;
    const warningCount = healthMetrics.filter(m => m.status === 'warning').length;
    
    if (errorCount > 0) return 'error';
    if (warningCount > 0) return 'warning';
    return 'healthy';
  };

  const getHealthScore = (): number => {
    if (!healthMetrics.length) return 100;
    
    const totalComponents = healthMetrics.length;
    const healthyComponents = healthMetrics.filter(m => m.status === 'healthy').length;
    const warningComponents = healthMetrics.filter(m => m.status === 'warning').length;
    
    // Healthy = 100%, Warning = 75%, Error/Maintenance = 0%
    const score = ((healthyComponents * 100) + (warningComponents * 75)) / totalComponents;
    return Math.round(score);
  };

  const getCriticalIssues = () => {
    return healthMetrics.filter(m => m.status === 'error');
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'healthy': return 'text-green-600';
      case 'warning': return 'text-yellow-600';
      case 'error': return 'text-red-600';
      case 'maintenance': return 'text-blue-600';
      default: return 'text-muted-foreground';
    }
  };

  return {
    healthMetrics,
    healthSummary,
    isLoading: isLoadingHealth || isLoadingSummary,
    isLoadingHealth,
    isLoadingSummary,
    isPerformingCheck,
    healthError,
    performHealthCheck: performHealthCheck.mutate,
    triggerRecovery: triggerRecovery.mutate,
    isRecovering: triggerRecovery.isPending,
    
    // Helper functions
    getOverallStatus,
    getHealthScore,
    getCriticalIssues,
    getStatusColor,
    
    // Computed values
    overallStatus: getOverallStatus(),
    healthScore: getHealthScore(),
    criticalIssues: getCriticalIssues(),
    
    // Component counts
    totalComponents: healthMetrics.length,
    healthyComponents: healthMetrics.filter(m => m.status === 'healthy').length,
    warningComponents: healthMetrics.filter(m => m.status === 'warning').length,
    errorComponents: healthMetrics.filter(m => m.status === 'error').length,
  };
};